// Lib
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';

// Actions
import { moveElementsToTrash } from '../actions/elementShortcutActions';
import { moveMultipleElements } from '../actions/elementMoveActions';
import { toggleInlineCommentPopup } from './inlineCommentActions';

// Selectors
import { getCurrentBoardId } from '../selectors/currentBoardSelector';

// Types
import type { InlineCommentOptions } from '../../../common/tiptap/extensions/inlineComment/InlineComment';
import { AttachMode } from '../../../common/elements/elementModelTypes';

// Constants
import { BoardSections } from '../../../common/boards/boardConstants';

type UseInlineCommentOptionsArgs = {
    // The element that owns the editor - inline comment threads are attached to it
    elementId?: string;
    isEditable?: boolean;
    clickableLinksWhenEditable?: boolean;
};

/**
 * Builds the InlineComment extension options for an element's editor.
 *
 * Inline comments live as marks in the editor content, but are backed by comment thread
 * elements attached inline to the element that owns the editor. Every editor that supports
 * them therefore needs the same dispatchers, to keep the marks and the thread elements in
 * sync through undo, redo and removal.
 */
const useInlineCommentOptions = ({
    elementId,
    isEditable,
    clickableLinksWhenEditable,
}: UseInlineCommentOptionsArgs): InlineCommentOptions => {
    const currentBoardId = useSelector(getCurrentBoardId);
    const dispatch = useDispatch();

    /**
     * When undoing the removal of an inline comment, we need to restore the comment thread
     * element that backs it, otherwise the popup will be empty.
     */
    const dispatchRestoreCommentThreads = useCallback(
        (commentThreadIds: string[], transactionId: number) => {
            dispatch(
                moveMultipleElements({
                    moves: commentThreadIds.map((commentThreadId) => ({
                        id: commentThreadId,
                        location: {
                            parentId: elementId,
                            section: BoardSections.ATTACHED,
                            position: {
                                attachMode: AttachMode.inline,
                            },
                        },
                    })),
                    moveOperation: undefined,
                    initialMeasurements: undefined,
                    transactionId,
                    actionOrigin: undefined,
                }),
            );
        },
        [dispatch, elementId],
    );

    const dispatchMoveCommentThreadsToTrash = useCallback(
        (commentThreadIds: string[], transactionId: number) => {
            dispatch(
                moveElementsToTrash({
                    elementIds: commentThreadIds,
                    currentBoardId,
                    keepSelection: true,
                    transactionId,
                }),
            );
        },
        [dispatch, currentBoardId],
    );

    const dispatchToggleInlineCommentPopup = useCallback(
        (commentThreadId: string) => dispatch(toggleInlineCommentPopup(commentThreadId, isEditable)),
        [dispatch, isEditable],
    );

    return useMemo(
        () => ({
            dispatchRestoreCommentThreads,
            dispatchMoveCommentThreadsToTrash,
            dispatchToggleInlineCommentPopup,
            getNewTransactionId,
            clickableLinksWhenEditable: !!clickableLinksWhenEditable,
        }),
        [
            dispatchRestoreCommentThreads,
            dispatchMoveCommentThreadsToTrash,
            dispatchToggleInlineCommentPopup,
            clickableLinksWhenEditable,
        ],
    );
};

export default useInlineCommentOptions;
